import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { fetchAnswerForSeek, invalidateSeekSession } from "src/redux/actions/seek";
import { fetchSeekLimit } from 'src/legacy/redux/actions/search';
import SeekSearch from "src/pages/seek/components/seekSearch";
import { SeekLandingPageProps, SeekSession, Chat } from "src/types/seekLandingpage";
import _ from "lodash";
import cx from "classnames";
import styles from "./style.module.scss";
import { useLocation } from "react-router";
import Truncate from "src/components/truncate";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { useSpring, animated, useTrail } from '@react-spring/web';
import { nanoid } from "nanoid";
import { SEEK_USER_QUESTION_LIMIT_EXCEEDED } from "src/constants/seekErrors";

export default function SeekLandingPage(props: SeekLandingPageProps) {
  const dispatch = useDispatch();
  const { trackSeekPromptClicked } = useSeekAdoption();

  const previousQueryRef = useRef<string>("");
  const streamName = _.get(props, "stream.name", "");  
  const account = useSelector((state: any) => _.get(state, 'accounts.item'));
  const isSeekEnabled = Boolean(_.get(account, "seekEnabled", false));
  const user = useSelector((state: any) => _.get(state, 'users.me', {}));
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get('query');
  const searchFields = queryParams.get('fields');
  const seekSession = useSelector((state: any) => _.get(state, 'seek.session.item')) as SeekSession | undefined;
  const isSeekLoading = Boolean(useSelector((state: any) => _.get(state, 'seek.session.loading')));
  const seekErrors = useSelector((state: any) => _.get(state, "legacyRootReducer.search.seek.error.errors")) as
    | Array<{ code: string | number }>
    | undefined;
  const isSeekLimitExceeded = Boolean(_.find(seekErrors, { code: SEEK_USER_QUESTION_LIMIT_EXCEEDED }));
  const seekSuggestedQuestions = (useSelector((state: any) => _.get(state, 'legacyRootReducer.search.seek.suggestions.items', [])) as string[]) || [];
  
  const [seekRedirect, setSeekRedirect] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [showSeekSearch, setShowSeekSearch] = useState<boolean>(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>(undefined);
  const [seekAnswerExits, setSeekAnswerExits] = useState(false);
  const chats = _.get(seekSession, "chats", []) as any;
  const compactView = _.size(searchFields) > 0 || _.size(searchQuery) > 0;

  const [showPrompt, setShowPrompt] = useState(compactView);
  const [hasSearched, setHasSearched] = useState(false);
  
const searchBarAnimation = useSpring({
  opacity: showPrompt ? 1 : 0,
  transform: showPrompt ? "translateY(0px)" : "translateY(30px)",
  immediate: compactView || hasSearched ,
  config: { tension: 220, friction: 28 },
});

const promptEntryAnimtaion = useTrail(seekSuggestedQuestions.length, {
  opacity: (showPrompt && !compactView) ? 1 : 0,
  delay: (compactView || hasSearched) ? 0 : 600,
  transform: (showPrompt && !compactView) ? "translateY(0px)" : "translateY(20px)",
  immediate: compactView || hasSearched ,
  config: { tension: 220, friction: 28 },
});

 const textAnimation = useSpring({
  transform: showPrompt ? "translateY(0px)" : "translateY(25px)",
  config: { tension: 220, friction: 28 },
  immediate: compactView || hasSearched
 })

  useEffect(() => {
    dispatch(fetchSeekLimit() as any);
  }, []);

 useEffect(() => {
  if (!compactView && !hasSearched) {
    const timer = setTimeout(() => {
      setShowPrompt(true);
    }, 3100); 
    return () => clearTimeout(timer);
  } else {
    setShowPrompt(true);
  }
}, [compactView, hasSearched]);

  useEffect(() => {
    if (compactView) {
      setHasSearched(true);
    }
    if (!compactView && _.size(searchQuery) === 0 && _.size(searchFields) === 0) {
      handleClearQuery();
    }
      if (!compactView) {
        dispatch(invalidateSeekSession() as any);
      }
  }, [compactView]);

  useEffect(() => {
    if(_.size(chats) > 0){
      const seekResponse = chats[0];
       if(!seekResponse?.loading && _.size(seekResponse?.answer) > 0 && seekResponse?.id){
           setSeekAnswerExits(true);
       }
    } else {
       setSeekAnswerExits(false);
    }
  }, [isSeekLoading ,chats])
  useEffect(() => {
    const query = _.get(props, "query", "") as string;
    if (_.size(query) > 0) {
      setQuery(query);
    }
  }, [_.get(props, "query")]);
 
  const handleSearchQuery = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (!isSeekLoading) {
      if (_.size(query) === 0) {
        dispatch(invalidateSeekSession() as any);
      }
      
    }

    props._handleSearch();
  };

  const handleClearQuery = () => {
    if (isSeekLoading || props.isFetching()) return
    setQuery("");
    previousQueryRef.current = "";  //For removing the stored query
    props.clearValue();
  };

  const handlePromptsClick = (question: string) => {
    setSelectedQuestion(question);
    setShowSeekSearch(true);
    trackSeekPromptClicked({ prompt: question });
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if(_.size(e.target.value.trim()) > 0) { // to avoid spaces & empty queries
         props._onQueryChange(e);
         setQuery(e.target.value.trim());
    } else {
      handleClearQuery();
    }
  };
  
  return (
  <>
    <div className={cx(styles.centeredSearchWrapper, {
      [styles.hasResults]: compactView,
      [styles.customFieldsAvailable]: props.isCustomFieldsAvailable,
    })}>
      <div className={styles.searchBoxContainer}>
          {!compactView && (
            <animated.div style={textAnimation}>
              <h3 className={styles.Name}>Hi {_.get(user, "firstName")}</h3>
              <div className={styles.subTitle}>
                <div className={cx(styles.firstLine, { [styles.animatingFirst]: !showPrompt })}>Good news! Your Hub just got smarter.</div>
                <div className={cx(styles.secondLine, { [styles.animatingSecond]: !showPrompt })}>You can now ask anything in plain language and we'll fetch the information for you.</div>
              </div>
            </animated.div>
          )}

          {showPrompt && (
            <animated.div
              className={cx(styles.column, styles.searchBar, { [styles.searchBarCompact]: compactView })}
         style={searchBarAnimation}>
          <form onSubmit={handleSearchQuery}>
            <div className={styles.inputContainer}>
              <div className={cx(styles.noPadding, { [styles.inputCompact]: compactView })}>
                <div className={styles.search}>
                    <label className={cx({ [styles.disabled]: isSeekLoading || props.isFetching() })}>
                        {props.isFromStream && (
                          <div className={cx(styles.inHub, { [styles.inHubCompactView]: compactView })}>
                            <Truncate className={cx(styles.tagName, styles.block)} width={100}>
                             {`in: ${streamName}`}
                            </Truncate>
                            <span className={styles.divider}></span>
                          </div>
                        )}

                        {props.isFromHub && (
                          <div className={cx(styles.inHub, { [styles.inHubCompactView]: compactView })}>
                            <span className={cx(styles.tagName, styles.block)}>
                              {"In: Hub"}
                            </span>
                            <span className={styles.divider}></span>
                          </div>
                        )}
                    
                    <textarea
                        className={cx(styles.field,)}
                        name="search"
                        style={{ resize: 'none' }}
                        value={props.query}
                        placeholder={"Ask anything..."}
                        onChange={handleQueryChange}
                        disabled={isSeekLoading || props.isFetching()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSearchQuery();
                          }
                        }}
                      />
                      {_.size(props.query) > 0 && compactView &&(
                        <div className={styles.inputActions}>
                          <i
                            onClick={handleClearQuery}
                            className={cx("paperflite-close", styles.clearIcon)}
                          />
                          <span className={styles.divider}></span>
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={isSeekLoading || props.isFetching()}
                        className={cx(styles.searchButton, {
                          [styles.searchButtonActive]: _.size(props.query) > 0
                        })}
                        onClick={(e) => {
                          e.preventDefault();
                          if (isSeekLoading || props.isFetching() || _.size(props.query) === 0) {
                            return;
                          }
                          handleSearchQuery();
                        }}
                      >
                        <i className="paperflite-right-arrow" />
                      </button>
                    </label>
                  </div>
              </div>
            </div>
          </form>
        </animated.div>
          )}

        {isSeekEnabled && !compactView && !props.isFetching() && _.size(seekSuggestedQuestions) > 0 && showPrompt && (
          <div className={cx(styles.promptsContainer, { [styles.hidden]: compactView })}>
            <div className={styles.prompts}>
              {promptEntryAnimtaion.map((style, index) => (
                  <animated.p
                    key={index}
                    style={{
                      ...style,
                      pointerEvents: style.opacity.to((o) => (o < 0.5 ? 'none' : 'auto')),
                    }}
                  onClick={() => handlePromptsClick(seekSuggestedQuestions[index])}
                  >
                    <Truncate lines={1} width={704}>{seekSuggestedQuestions[index]}</Truncate>
                 </animated.p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    {showSeekSearch && (
      <SeekSearch
        question={selectedQuestion ?? ""}
        onClose={() => {
          setShowSeekSearch(false);
          setSeekRedirect(false);
          setSelectedQuestion(undefined);
          const previousQuery = previousQueryRef.current;
          if (_.size(previousQuery) > 0) {
            setQuery(previousQuery);
            props._onQueryChange({ target: { value: previousQuery } } as React.ChangeEvent<HTMLTextAreaElement>);
          }
        }}
        isLimitExceeded={isSeekLimitExceeded}
      />
    )}
  </>
);
}